import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import {
  FEATURED_CAP_FALLBACK, endOfRiyadhDay, slotsLeftFromError, isActiveFeature,
  fetchFeatured, featureProducts, unfeatureProducts, featureSeller, searchSellers,
} from '../../lib/catalogFeatured';

// Featured products (catalog ordering step B, owner 2026-10-03). Featured
// products open the catalog for every trader, at most 25 at once, each with an
// optional end date after which it drops back on its own (no cron). Single
// products are featured from their page (the ⭐ admins see there); this page
// is the list, the end dates, and featuring one supplier's whole range.
// "Feature a whole category" is deliberately not here (owner: skip for now).
export default function ConsoleFeatured({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [term, setTerm] = useState('');
  const [sellers, setSellers] = useState([]);
  const [bulkUntil, setBulkUntil] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const load = useCallback(async () => {
    try { setRows(await fetchFeatured()); } catch (e) { console.error('[ConsoleFeatured] load failed:', e?.message || e); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try { const s = await searchSellers(term); if (!cancelled) setSellers(s); }
      catch (e) { console.error('[ConsoleFeatured] seller search failed:', e?.message || e); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [term]);

  const active = useMemo(() => (rows || []).filter((r) => isActiveFeature(r) && r.product), [rows]);
  const inactive = useMemo(() => (rows || []).filter((r) => !isActiveFeature(r) || !r.product), [rows]);

  const report = (error, okMsg) => {
    if (!error) { flash(okMsg); return; }
    const left = slotsLeftFromError(error);
    flash(left != null
      ? (isAr ? `لا توجد خانات كافية — المتبقي ${left} من ${FEATURED_CAP_FALLBACK}.` : `Not enough slots — ${left} of ${FEATURED_CAP_FALLBACK} left.`)
      : (isAr ? 'تعذّر الحفظ.' : 'Could not save.'));
    if (left == null) console.error('[ConsoleFeatured] write failed:', error.message);
  };

  const setUntil = async (r, ymd) => {
    setBusy(true);
    const { error } = await featureProducts([{ source: r.source, product_id: r.product_id }], endOfRiyadhDay(ymd));
    setBusy(false); report(error, isAr ? 'تم تحديث تاريخ الانتهاء.' : 'End date updated.'); load();
  };
  const remove = async (r) => {
    setBusy(true);
    const { error } = await unfeatureProducts([{ source: r.source, product_id: r.product_id }]);
    setBusy(false); report(error, isAr ? 'أُلغي التمييز.' : 'Unfeatured.'); load();
  };
  const bulkSeller = async (s) => {
    setBusy(true);
    const { error } = await featureSeller(s.source, s.seller_id, endOfRiyadhDay(bulkUntil));
    setBusy(false); report(error, isAr ? `مُيّزت منتجات ${s.name}.` : `Featured ${s.name}'s products.`); load();
  };

  const name = (p) => (isAr ? (p?.name_ar || p?.name_en) : (p?.name_en || p?.name_ar)) || '—';
  const seller = (p) => (p?.seller_name_latin || '').trim() || p?.seller_name || '';
  const ymd = (iso) => (iso ? new Date(new Date(iso).getTime() + 3 * 3600e3).toISOString().slice(0, 10) : '');

  const Row = ({ r }) => (
    <div className="ac-card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, marginTop: 8, flexWrap: 'wrap' }}>
      {r.product?.image ? <img src={r.product.image} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} /> : null}
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5 }}>{r.product ? name(r.product) : (isAr ? 'لم يعد في الكتالوج' : 'No longer in the catalog')}</div>
        <div style={{ color: 'var(--ac-muted)', fontSize: 12 }}>
          {seller(r.product)} · {r.source === 'supplier' ? (isAr ? 'مورد' : 'supplier') : (isAr ? 'مصنع' : 'factory')}
        </div>
      </div>
      <label style={{ fontSize: 12, color: 'var(--ac-muted)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        {isAr ? 'حتى' : 'Until'}
        <input type="date" defaultValue={ymd(r.featured_until)} disabled={busy}
          onChange={(e) => setUntil(r, e.target.value)} />
      </label>
      <button className="ac-btn ac-btn-sm" disabled={busy} onClick={() => remove(r)}>{isAr ? 'إلغاء التمييز' : 'Unfeature'}</button>
    </div>
  );

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="featured">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'المنتجات المميّزة' : 'Featured products'}</h1>
            <p className="ac-sub">
              {isAr
                ? `تظهر أولاً في الكتالوج لكل تاجر. المستخدم ${active.length} من ${FEATURED_CAP_FALLBACK}. ميّز منتجاً واحداً من صفحته (⭐).`
                : `Shown first in the catalog to every trader. ${active.length} of ${FEATURED_CAP_FALLBACK} used. Feature a single product from its page (⭐).`}
            </p>
          </div>
        </div>

        <div className="ac-card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{isAr ? 'تمييز كل منتجات مورد أو مصنع' : "Feature one seller's whole range"}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="ac-input" value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder={isAr ? 'اسم المورد أو المصنع' : 'Supplier or factory name'} style={{ flex: '1 1 240px' }} />
            <label style={{ fontSize: 12, color: 'var(--ac-muted)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              {isAr ? 'حتى (اختياري)' : 'Until (optional)'}
              <input type="date" value={bulkUntil} onChange={(e) => setBulkUntil(e.target.value)} />
            </label>
          </div>
          {sellers.map((s) => (
            <div key={`${s.source}:${s.seller_id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 13 }}>
              <span style={{ flex: 1 }}>{s.name} <span style={{ color: 'var(--ac-muted)' }}>· {s.count} {isAr ? 'منتج' : 'products'}</span></span>
              <button className="ac-btn ac-btn-sm" disabled={busy} onClick={() => bulkSeller(s)}>{isAr ? 'تمييز الكل' : 'Feature all'}</button>
            </div>
          ))}
        </div>

        {rows == null ? <div className="ac-skel" style={{ height: 90 }} />
          : active.length === 0 ? <div className="ac-placeholder">{isAr ? 'لا منتجات مميّزة.' : 'No featured products.'}</div>
          : active.map((r) => <Row key={`${r.source}:${r.product_id}`} r={r} />)}

        {inactive.length > 0 && (
          <>
            <h2 className="ac-h1" style={{ fontSize: 15, marginTop: 24 }}>{isAr ? 'منتهية أو خارج الكتالوج' : 'Expired or no longer in the catalog'}</h2>
            {inactive.map((r) => <Row key={`${r.source}:${r.product_id}`} r={r} />)}
          </>
        )}
      </div>
      {toast && <div className="ac-toast" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{toast}</div>}
    </ConsoleShell>
  );
}
